import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * SignInModal — compact sign-in dialog for use anywhere in a product.
 *
 * Handles email/password login and social sign-in.
 * For forgot password or more complex flows, links to the full /login page.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { signIn } from "aws-amplify/auth";
import { Eye, EyeOff, Lock, Mail } from "lucide-react";
import { useAuth } from "../AuthProvider.js";
import { MAX_EMAIL_LENGTH, MAX_PASSWORD_LENGTH } from "../inputLimits.js";
import { AuthAlert } from "./AuthAlert.js";
import { AuthField } from "./AuthField.js";
import { SocialSignIn } from "./SocialSignIn.js";
// =============================================================================
// Default social providers
// =============================================================================
const DEFAULT_GOOGLE_PROVIDER = {
    id: "Google",
    label: "Google",
    leading: (_jsx("span", { className: "mr-2 flex size-5 items-center justify-center rounded-full border border-slate-200 bg-white text-xs font-semibold", children: "G" })),
};
// =============================================================================
// Component
// =============================================================================
export function SignInModal({ open, onOpenChange, socialProviders = [DEFAULT_GOOGLE_PROVIDER], signupPath = "/signup", loginPath = "/login", }) {
    const { loginWith, refreshSession } = useAuth();
    const navigate = useNavigate();
    const dialogRef = useRef(null);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const canLogin = Boolean(email.trim() && password && !isSubmitting);
    const handleClose = useCallback(() => {
        setEmail("");
        setPassword("");
        setShowPassword(false);
        setErrorMessage(null);
        onOpenChange(false);
    }, [onOpenChange]);
    useEffect(() => {
        const dialog = dialogRef.current;
        if (!dialog)
            return;
        if (open && !dialog.open) {
            dialog.showModal();
        }
        else if (!open && dialog.open) {
            dialog.close();
        }
    }, [open]);
    useEffect(() => {
        const dialog = dialogRef.current;
        if (!dialog)
            return;
        const onClose = () => handleClose();
        dialog.addEventListener("close", onClose);
        return () => dialog.removeEventListener("close", onClose);
    }, [handleClose]);
    const handleLogin = async () => {
        if (!canLogin)
            return;
        setIsSubmitting(true);
        setErrorMessage(null);
        try {
            const result = await signIn({ username: email.trim(), password });
            if (result.isSignedIn) {
                await refreshSession();
                handleClose();
                return;
            }
            handleClose();
            navigate(loginPath);
        }
        catch (error) {
            setErrorMessage(error instanceof Error ? error.message : "Login failed.");
        }
        finally {
            setIsSubmitting(false);
        }
    };
    const handleKeyDown = (e) => {
        if (e.key === "Enter")
            void handleLogin();
    };
    if (!open)
        return null;
    return (_jsxs("dialog", { ref: dialogRef, className: "fixed inset-0 z-50 m-auto max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-xl backdrop:bg-black/50", onClick: (e) => {
            if (e.target === dialogRef.current)
                handleClose();
        }, children: [_jsx("div", { className: "mx-auto flex size-12 items-center justify-center rounded-full bg-violet-100 text-violet-600", children: _jsx(Lock, { className: "size-5" }) }), _jsx("h2", { className: "mt-4 text-center text-2xl font-semibold text-slate-900", children: "Welcome back" }), _jsx("p", { className: "mt-1 text-center text-sm text-slate-600", children: "Log in to your account to continue" }), errorMessage && _jsx(AuthAlert, { variant: "error", children: errorMessage }), _jsxs("div", { className: "mt-4 space-y-4", onKeyDown: handleKeyDown, children: [_jsx(AuthField, { id: "modal-login-email", label: "Email address", type: "email", autoComplete: "email", placeholder: "you@example.com", value: email, onChange: setEmail, icon: _jsx(Mail, { className: "size-4" }), maxLength: MAX_EMAIL_LENGTH }), _jsx(AuthField, { id: "modal-login-password", label: "Password", type: showPassword ? "text" : "password", autoComplete: "current-password", placeholder: "Enter your password", value: password, onChange: setPassword, icon: _jsx(Lock, { className: "size-4" }), maxLength: MAX_PASSWORD_LENGTH, rightSlot: _jsx("button", { type: "button", "aria-label": showPassword ? "Hide password" : "Show password", onClick: () => setShowPassword((p) => !p), className: "transition hover:text-slate-600", children: showPassword ? _jsx(EyeOff, { className: "size-4" }) : _jsx(Eye, { className: "size-4" }) }) }), _jsx("button", { type: "button", onClick: handleLogin, disabled: !canLogin, className: "h-11 w-full rounded-xl text-sm font-medium text-white transition-colors bg-violet-600 hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50", children: isSubmitting ? "Signing in..." : "Log in" }), socialProviders.length > 0 && (_jsx(SocialSignIn, { title: "Or continue with", providers: socialProviders, columns: 1, onProviderLogin: loginWith })), _jsxs("div", { className: "space-y-1 text-center text-sm text-slate-600", children: [_jsxs("p", { children: ["Don't have an account?", " ", _jsx(Link, { to: signupPath, onClick: handleClose, className: "font-medium text-violet-600 hover:text-violet-700", children: "Sign up" })] }), _jsx("p", { children: _jsx(Link, { to: loginPath, onClick: handleClose, className: "text-xs text-slate-400 underline underline-offset-2 hover:text-slate-600", children: "Forgot password or more options" }) })] })] })] }));
}
